import { useState, useEffect } from "react";
import {
  Container,
  Tabs,
  Title,
  Group,
  Text,
  Alert,
  Paper,
} from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import { HiExclamationCircle } from "react-icons/hi2";
import { useGetAllPermissionsQuery } from "../hooks/queries/usePermissionQueries";
import PermissionsTab from "../components/RBAC/tabs/PermissionsTab";
import RolesTab from "../components/RBAC/tabs/RolesTab";
import { useGetAllRolesQuery } from "../hooks/queries/useRoleQueries";
import { useGetAllUsersQuery } from "../hooks/queries/useProfileQueries";
import UserRolesTab from "../components/RBAC/tabs/UserRolesTab";
import RBACManagementTabs from "../components/RBAC/TabNavigation";

const RBACManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("permissions");

  // Initialize tab from URL parameter on component mount
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["permissions", "roles", "users"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Update URL when tab changes
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  // Fetch real permissions count
  const { data: permissions = [] } = useGetAllPermissionsQuery();
  const { data: roles = [] } = useGetAllRolesQuery();
  const { data: users = [] } = useGetAllUsersQuery();

  // Real counts for tab badges
  const tabCounts = {
    permissions: permissions.length,
    roles: roles.length,
    users: users.length,
  };
  return (
    <div className="min-h-screen flex flex-col pb-16">
      <Paper shadow="xs" p="xl">
        <div className="flex-shrink-0 py-6 px-6">
          <Group justify="space-between" className="mb-8">
            <div>
              <Title
                order={4}
                className="text-2xl font-bold text-gray-900 mb-2"
              >
                Role-Based Access Control (RBAC)
              </Title>
              <Text c="dimmed" size="sm">
                Manage permissions, roles, and user access across your system.
              </Text>

              <Alert
                icon={<HiExclamationCircle size={16} />}
                title="RBAC Management System"
                color="blue"
                variant="light"
                className="mt-4"
              >
                <Text size="xs">
                  Use the tabs below to manage system permissions, define roles,
                  and assign users to appropriate access levels.
                </Text>
              </Alert>
            </div>
          </Group>

          <RBACManagementTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabCounts={tabCounts}
          />
        </div>

        <div className="flex-1 px-6 pb-6">
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            color="blue"
            className="h-full"
          >
            <Tabs.Panel value="permissions" className="h-full">
              <PermissionsTab />
            </Tabs.Panel>
            <Tabs.Panel value="roles" className="h-full">
              <RolesTab />
            </Tabs.Panel>
            <Tabs.Panel value="users" className="h-full">
              <UserRolesTab />
            </Tabs.Panel>
          </Tabs>
        </div>
      </Paper>
    </div>
  );
};

export default RBACManagement;
